/**
 * guild_claims.ts — the guilds' claims as the client sees them (2026-09-24): `guild_territory` rows named the way the
 * server words them ("the Iron Writ guild", "the Tillers of Veldea") and flagged with whether the viewer is a member —
 * the placement judge's build rights, the map's guild filter and the world's rim banners all read this one list.
 */
import type { DbConnection } from '../module_bindings/index.js';
import type { GuildClaim } from './placement_rules.js';

interface IdLike { isEqual(o: unknown): boolean }
interface TerrRow { territoryKey: string; kind: string; guildId: bigint; worldId: bigint; x: number; z: number; tier: number; radius: number }
type Db = {
  guildTerritory?: { iter(): Iterable<TerrRow> };
  guild?: { guildId: { find(id: bigint): { name: string; tag: string } | undefined } };
  versonaGuild?: { vguildId: { find(id: bigint): { name: string; tag: string; trade: string } | undefined } };
  guildMember?: { guildMemberByIdentity: { filter(id: unknown): Iterable<{ guildId: bigint }> } };
};

/** The viewer's player guild (null when none). */
export function myGuildId(conn: DbConnection, me: IdLike | null): bigint | null {
  const db = conn.db as unknown as Db;
  if (!me || !db.guildMember) return null;
  try { for (const m of db.guildMember.guildMemberByIdentity.filter(me)) return m.guildId; } catch { /* pre-publish */ }
  return null;
}

export interface ClaimView extends GuildClaim { tier: number; tag: string; trade: string; worldId: bigint }

export function claimsFor(conn: DbConnection, me: IdLike | null, worldId: bigint | null = null): ClaimView[] {
  const db = conn.db as unknown as Db;
  if (!db.guildTerritory) return [];
  const mine = myGuildId(conn, me);
  const out: ClaimView[] = [];
  try {
    for (const t of db.guildTerritory.iter()) {
      if (worldId !== null && t.worldId !== worldId) continue;
      if (t.kind === 'player') {
        const g = db.guild?.guildId.find(t.guildId);
        out.push({ key: t.territoryKey, kind: 'player', guildId: t.guildId, name: g ? `the ${g.name} guild` : 'a guild', x: t.x, z: t.z, r: t.radius, member: mine !== null && mine === t.guildId, tier: t.tier, tag: g?.tag ?? '', trade: '', worldId: t.worldId });
      } else {
        const v = db.versonaGuild?.vguildId.find(t.guildId);
        out.push({ key: t.territoryKey, kind: 'versona', guildId: t.guildId, name: v ? v.name.replace(/^The /, 'the ') : 'a trade guild', x: t.x, z: t.z, r: t.radius, member: false, tier: t.tier, tag: v?.tag ?? '', trade: v?.trade ?? '', worldId: t.worldId });
      }
    }
  } catch { /* pre-publish */ }
  return out;
}
